import requestApis from "apis/request";
import {
  IAttachment,
  IBotReply,
  IBusinessRecordRef,
  IInternalLookup,
  IInternalLookupLayer,
  IScreenContext,
  ISupportRequest,
  PilotModule,
  SupportRequestStatus,
} from "models/support-channel";

/**
 * OMNI-TIC2026 · T46 — tầng API thật của Support Channel. Đây là chỗ DUY NHẤT trong FE biết hình
 * dạng wire; `hooks/support-channel.ts` và mọi component chỉ thấy type trong `models/`.
 *
 * Contract: `app/bff/staff-service/api/support_assistant/support_assistant.proto` (B1–B11).
 * Hai prefix, hai audience, và chúng KHÔNG thay nhau được:
 *   /api/v1/staff-seller/**  — người của seller, hỏi bot + xem yêu cầu CỦA MÌNH
 *   /api/v1/internal/**      — Support của Teko, đọc/xử lý yêu cầu của người khác
 *
 * Ba điều đáng biết trước khi đọc tiếp, vì chúng giải thích gần hết code dưới đây:
 *
 *  1. **Khoá của mọi lệnh GHI là `issueId`, không phải mã `SP-…`** (L1-013). Mã support là giá trị
 *     BFF DẪN XUẤT để người đọc, không phải khoá tra cứu. Nên `ISupportRequest.code` giữ mã để hiện,
 *     và `issueId` được mang kèm — không có nó thì không accept/reply/complete được gì.
 *  2. **Từ vựng trạng thái của server rộng hơn của FE.** Server có `closed_pending_review` (đã đóng
 *     nhưng chưa ghi nghiệm thu — ô (1,0) của bảng view, sinh ra khi B11 chết giữa hai lời gọi Core).
 *     FE phải có tên riêng cho nó; gộp vào `completed` là nói với người dùng một điều chưa đúng.
 *  3. **`total = -1` nghĩa là CHƯA ĐẾM**, không phải 0 dòng. Chỉ có số thật khi
 *     `isGetTotalRecord=true`. Trả -1 ra UI thì màn hình sẽ hiện "-1 kết quả", nên nó bị chặn ở đây.
 */

const { staffBffClient } = requestApis;

const STAFF_SELLER_BASE = "/api/v1/staff-seller/support-channel";
const INTERNAL_BASE = "/api/v1/internal/support-requests";
// B13 sống dưới `support-channel`, không phải `support-requests`: nó chạy được KHÔNG CẦN phiếu nào
// (BR-BP3-13, đường vào (2) của BP3), nên nó không phải một hành động trên một request.
const INTERNAL_LOOKUP_PATH = "/api/v1/internal/support-channel/lookups";

/**
 * Một lượt hỏi AI đi qua hai service (staff-bff → omni_agents_service → conversation-service) và
 * budget hai lượt phía BFF là 55s. Timeout mặc định của axios client dùng chung ngắn hơn thế, nên
 * nếu không nâng ở đây thì FE sẽ bỏ cuộc trước cả khi BFF kịp trả lời — và người dùng thấy "lỗi
 * mạng" cho một câu trả lời thật ra đã về.
 */
const AI_TURN_TIMEOUT_MS = 60_000;

/**
 * B13 là **HAI** lượt AI nối tiếp (phân tích nội bộ → gợi ý trả lời), nên nó không dùng chung ngân
 * sách với B1. BFF cấp `singleTurnTimeout` cho MỖI lượt, tức trần phía server là ~2×; đặt bằng 60s ở
 * đây thì FE bỏ cuộc giữa lượt thứ hai và người dùng thấy "lỗi mạng" cho một phân tích đã xong.
 */
const INTERNAL_LOOKUP_TIMEOUT_MS = 2 * AI_TURN_TIMEOUT_MS;

/* ------------------------------------------------------------------ wire types */

interface IEnvelope<T> {
  code: number;
  traceId: string;
  message: string;
  data: T;
}

interface IWireCitation {
  title: string;
  version: string;
  url?: string | null;
}

interface IWireAnswer {
  disposition: "answer" | "clarify" | "limited" | "refused";
  content: string;
  citations?: IWireCitation[] | null;
  isEscalationOffered: boolean;
}

/** Payload của B13. Hai object RIÊNG, mỗi cái một `citations[]` — không phải một answer gộp. */
interface IWireLookupData {
  internalAnalysis: IWireAnswer;
  suggestedReply?: IWireAnswer | null;
  suggestionId?: string | null;
  conversationId: string;
}

interface IWireSupportReply {
  content: string;
  senderName: string;
  repliedAt: number;
}

interface IWireAttachment {
  fileIndex: number;
  fileName: string;
}

interface IWireRequestDetail {
  issueId: number;
  supportCode: string;
  status: string;
  module: string;
  summaryText: string;
  createdAt: number;
  supportReply?: IWireSupportReply | null;
}

interface IWireInternalDetail {
  issueId: number;
  supportCode: string;
  status: string;
  requesterName: string;
  sellerName: string;
  module: string;
  summaryText: string;
  createdAt: number;
  assigneeName?: string | null;
  attachments?: IWireAttachment[] | null;
  supportReply?: IWireSupportReply | null;
  /** Giây unix lúc người đang xử lý TIẾP NHẬN phiếu; null khi chưa ai nhận. Xem `toIso`: là CHUỖI. */
  acceptedAt?: number | string | null;
}

interface IWireListRow {
  issueId: number;
  supportCode: string;
  requesterName: string;
  sellerName: string;
  module: string;
  summaryText: string;
  attachmentCount?: number | null;
  status: string;
  hasCustomerReply?: boolean | null;
  assigneeName?: string | null;
  acceptedAt?: number | null;
  createdAt: number;
  isCreatedOnBehalf: boolean;
}

export interface IInternalSessionScope {
  capabilities: string[];
  allowedSellers: { sellerId: number; sellerName: string }[];
}

interface IWireListData {
  requests?: IWireListRow[] | null;
  session?: IInternalSessionScope | null;
  tabCounts?: { open: number; unassigned: number; mine: number; done: number } | null;
  page: number;
  pageSize: number;
  total: number;
}

/* ------------------------------------------------------------------ mapping */

/**
 * Trạng thái phía server → phía FE.
 *
 * `closed_pending_review` KHÔNG gộp vào `completed`: nó là "đã đóng nhưng chưa ghi nghiệm thu", một
 * trạng thái vận hành thật mà người xử lý phải thấy để bấm hoàn thành lại (B11 forward-recovery).
 * Giá trị lạ ⇒ giữ nguyên `sent` chứ không đoán: đoán sai ở đây làm UI mở/khoá nút sai.
 */
const STATUS_FROM_WIRE: Record<string, SupportRequestStatus> = {
  submitted: "sent",
  accepted: "acknowledged",
  replied: "replied",
  closed_pending_review: "closed_pending_review",
  completed: "completed",
  cancelled: "cancelled",
};

const toStatus = (wire: string): SupportRequestStatus => STATUS_FROM_WIRE[wire] || "sent";

/**
 * Chiều NGƯỢC lại — cho bộ lọc `status` của B7 (#5).
 *
 * Nó phải tồn tại vì HAI PHÍA NÓI HAI TỪ VỰNG: contract dùng `submitted`/`accepted`
 * (`SupportRequestStatus` trong openapi.yaml), còn FE dùng `sent`/`acknowledged`. Gửi thẳng giá trị
 * của FE lên là gửi một chuỗi server không bao giờ khớp — và nó hỏng ÂM THẦM: proto không ràng buộc
 * enum cho field này (khác `module`), nên không có 400 nào bật lên, chỉ là bộ lọc không lọc gì.
 *
 * SINH TỪ `STATUS_FROM_WIRE` chứ không viết tay bảng thứ hai: hai bảng độc lập sẽ trôi khỏi nhau, và
 * lần trôi đó cũng im lặng đúng như vậy.
 */
const STATUS_TO_WIRE: Partial<Record<SupportRequestStatus, string>> = Object.keys(STATUS_FROM_WIRE).reduce(
  (acc: Partial<Record<SupportRequestStatus, string>>, wire: string) => {
    acc[STATUS_FROM_WIRE[wire]] = wire;
    return acc;
  },
  {}
);

/** Server trả module dạng chuỗi mở; FE chỉ nhận đúng ba module pilot. */
const PILOT: PilotModule[] = ["orders", "products", "promotions"];
const toModule = (wire: string): PilotModule | undefined =>
  PILOT.indexOf(wire as PilotModule) >= 0 ? (wire as PilotModule) : undefined;

/**
 * Unix GIÂY → ISO. Không phải milli, và không phải số: proto3 JSON tuần tự hoá `int64` thành
 * CHUỖI, nên trên dây các mốc này về dạng `"1787306400"`. Đo bằng chính marshaler của gateway
 * (`runtime.JSONPb`), không phải suy đoán từ kiểu proto.
 *
 * Chặn NaN chứ không để `new Date(NaN).toISOString()` chạy: nó **ném RangeError**, và một mốc rác
 * ở một field sẽ đánh sập cả màn thay vì chỉ mất một dòng thời gian.
 */
const toIso = (unixSeconds?: number | string | null): string | undefined => {
  const seconds = Number(unixSeconds);
  return unixSeconds && !isNaN(seconds) ? new Date(seconds * 1000).toISOString() : undefined;
};

const toReply = (wire?: IWireSupportReply | null): ISupportRequest["supportReply"] =>
  wire && wire.content ? { text: wire.content, author: wire.senderName, at: toIso(wire.repliedAt) || "" } : undefined;

/**
 * Đính kèm ở đường đọc chỉ có index + tên — BFF cố ý không trả url lưu trữ (tải bytes đi qua B12).
 * `sizeLabel` để rỗng vì server không trả kích thước: hiện "0 KB" là bịa một con số.
 */
const toAttachments = (wire?: IWireAttachment[] | null): IAttachment[] =>
  (wire || []).map((file) => ({
    id: String(file.fileIndex),
    name: file.fileName,
    sizeLabel: "",
    kind: "file",
    state: "ready",
  }));

const toBotReply = (answer: IWireAnswer, conversationId: string): IBotReply => ({
  messageId: `${conversationId}-${answer.disposition}-${Date.now()}`,
  kind: answer.disposition,
  text: answer.content,
  // Chỉ `answer` mới có nguồn (BR-BP1-08). BFF đã cưỡng chế điều đó trước khi serialize; ở đây lọc
  // lại một lần nữa là rẻ, và nó chặn đúng cái sai nguy hiểm nhất: render một dẫn chiếu bên cạnh
  // một câu KHÔNG phải câu trả lời.
  citations:
    answer.disposition === "answer"
      ? (answer.citations || []).map((c) => ({ title: c.title, version: c.version }))
      : [],
  offersHandoff: answer.isEscalationOffered,
});

/**
 * Map một lớp của B13. Cùng luật citations như `toBotReply`: chỉ `answer` mới được mang nguồn.
 *
 * Không tái dùng `toBotReply` vì nó sinh `messageId` bằng `Date.now()` và mang `offersHandoff` —
 * hai thứ của luồng chat Staff Seller, vô nghĩa ở đây và sẽ làm hai lớp trông như hai tin nhắn.
 */
const toLookupLayer = (answer: IWireAnswer): IInternalLookupLayer => ({
  kind: answer.disposition,
  text: answer.content,
  citations:
    answer.disposition === "answer"
      ? (answer.citations || []).map((c) => ({ title: c.title, version: c.version }))
      : [],
});

const toRequest = (wire: IWireRequestDetail): ISupportRequest => ({
  code: wire.supportCode,
  issueId: wire.issueId,
  status: toStatus(wire.status),
  summary: wire.summaryText,
  attachments: [],
  module: toModule(wire.module),
  sentAt: toIso(wire.createdAt),
  supportReply: toReply(wire.supportReply),
});

const toInternalRequest = (wire: IWireInternalDetail): ISupportRequest => ({
  code: wire.supportCode,
  issueId: wire.issueId,
  status: toStatus(wire.status),
  summary: wire.summaryText,
  attachments: toAttachments(wire.attachments),
  module: toModule(wire.module),
  requesterName: wire.requesterName,
  sellerName: wire.sellerName,
  sentAt: toIso(wire.createdAt),
  acknowledgedBy: wire.assigneeName || undefined,
  acknowledgedAt: toIso(wire.acceptedAt),
  supportReply: toReply(wire.supportReply),
});

const toListItem = (wire: IWireListRow): ISupportRequest => ({
  code: wire.supportCode,
  issueId: wire.issueId,
  status: toStatus(wire.status),
  summary: wire.summaryText,
  attachments: [],
  module: toModule(wire.module),
  requesterName: wire.requesterName,
  sellerName: wire.sellerName,
  sentAt: toIso(wire.createdAt),
  acknowledgedBy: wire.assigneeName || undefined,
  acknowledgedAt: toIso(wire.acceptedAt),
  createdOnBehalf: wire.isCreatedOnBehalf,
  // `attachmentCount` của server là `null = CHƯA BIẾT` (payload danh sách của Core không có thông
  // tin file), khác hẳn 0 = không có file. Nên nó KHÔNG được dựng thành mảng rỗng ở đây — màn danh
  // sách đọc `attachmentCount` trực tiếp.
  attachmentCount: typeof wire.attachmentCount === "number" ? wire.attachmentCount : undefined,
  hasCustomerReply: typeof wire.hasCustomerReply === "boolean" ? wire.hasCustomerReply : undefined,
});

/* ------------------------------------------------------------------ B1 · B2 · B3 · B4 (staff seller) */

const toBusinessContext = (record?: IBusinessRecordRef) =>
  record ? { type: record.type, code: record.code } : undefined;

export interface IAskResult {
  reply: IBotReply;
  /** Token đối thoại đã ký. Lượt sau PHẢI gửi lại đúng chuỗi này. */
  conversationId: string;
}

/**
 * B1 — một lượt hỏi.
 *
 * `conversationId` là token BFF ký theo `iamSub`, không phải id thô. FE không sinh, không sửa, không
 * đoán nó: lượt đầu gửi null, các lượt sau echo lại đúng chuỗi server trả. Đó là thứ duy nhất ngăn
 * transcript của người này lọt sang người khác (omni_agents_service không có middleware auth nào).
 */
const ask = async (
  question: string,
  context: IScreenContext,
  conversationId?: string,
  attachmentIds?: string[]
): Promise<IAskResult> => {
  const { data: envelope } = await staffBffClient.post<IEnvelope<{ answer: IWireAnswer; conversationId: string }>>(
    `${STAFF_SELLER_BASE}/questions`,
    {
      question,
      screenName: context.screenName,
      module: context.module,
      businessContext: toBusinessContext(context.record),
      conversationId: conversationId || undefined,
      // Token B5 ký, tối đa 3 (contract `max_items: 3`) — chỉ luồng trợ lý v2 đọc được, và BFF từ
      // chối RÕ khi luồng đang chạy là v1 thay vì im lặng hỏi thiếu ảnh. Không đặt khoá khi rỗng.
      attachmentIds: attachmentIds && attachmentIds.length ? attachmentIds : undefined,
    },
    { timeout: AI_TURN_TIMEOUT_MS }
  );
  const data = envelope.data;
  return { reply: toBotReply(data.answer, data.conversationId), conversationId: data.conversationId };
};

export interface IUploadedAttachment {
  fileName: string;
  attachmentId: string;
}

/**
 * B5 — upload MỘT tệp đính kèm.
 *
 * Server trả `{fileName, attachmentId}`; `attachmentId` là token BFF ký ({iamSub, sellerId, url,
 * fileName, mime, exp=+2h}) — KHÔNG phải url (D-12). FE giữ token nguyên văn và gửi lại y nguyên ở
 * B1 (hỏi kèm ảnh) hoặc B3 (tạo phiếu). Server kiểm loại/kích thước trên BYTES đã sniff, nên client
 * validate chỉ là phản hồi nhanh, không phải cổng.
 */
const uploadAttachment = async (file: File): Promise<IUploadedAttachment> => {
  const formData = new FormData();
  formData.append("file", file);
  const { data: envelope } = await staffBffClient.post<IEnvelope<IUploadedAttachment>>(
    `${STAFF_SELLER_BASE}/attachments`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return envelope.data;
};

/** B2 — AI soạn bản tóm tắt để NGƯỜI đọc và sửa. Nó là bản nháp, không phải bản gửi (BR-BP2-03). */
const draftSummary = async (conversationId: string): Promise<string> => {
  const { data: envelope } = await staffBffClient.post<IEnvelope<{ summaryText: string }>>(
    `${STAFF_SELLER_BASE}/escalation-summary`,
    { conversationId },
    { timeout: AI_TURN_TIMEOUT_MS }
  );
  return envelope.data.summaryText;
};

/**
 * B3 — tạo yêu cầu. Đường DUY NHẤT sinh mã support, và chỉ chạy sau khi người dùng xác nhận
 * (BR-BP2-04).
 *
 * `summaryText` là bản NGƯỜI xác nhận, có thể khác bản AI nháp — nên nó được truyền vào tường minh
 * thay vì lấy lại từ draft.
 */
const submitRequest = async (
  conversationId: string,
  summaryText: string,
  module: PilotModule,
  attachmentIds: string[] = []
): Promise<ISupportRequest> => {
  const { data: envelope } = await staffBffClient.post<
    IEnvelope<{ issueId: number; supportCode: string; status: string }>
  >(`${STAFF_SELLER_BASE}/support-requests`, { conversationId, summaryText, module, attachmentIds });
  const data = envelope.data;
  return {
    code: data.supportCode,
    issueId: data.issueId,
    status: toStatus(data.status),
    summary: summaryText,
    attachments: [],
    module,
  };
};

/** B4 — người gửi xem yêu cầu CỦA MÌNH. Yêu cầu của đồng nghiệp cùng seller trả 404, đúng chủ ý. */
const getMyRequest = async (issueId: number): Promise<ISupportRequest> => {
  const { data: envelope } = await staffBffClient.get<IEnvelope<IWireRequestDetail>>(
    `${STAFF_SELLER_BASE}/support-requests/${issueId}`
  );
  return toRequest(envelope.data);
};

/* ------------------------------------------------- E1 · yêu cầu CỦA TÔI (danh sách) */

/**
 * `GET /api/v1/issues` với `flow=support_channel`.
 *
 * Ba điều về endpoint này khác hẳn phần còn lại của file, và cả ba đều là chủ ý:
 *
 *  1. **Nó KHÔNG nằm dưới `/staff-seller/support-channel`.** Nó thuộc epic ERP2020-385 và đã phục vụ
 *     ERP Staff Desktop + SPOS từ lâu. Epic này chỉ thêm MỘT tham số `flow` để hẹp danh sách về đúng
 *     luồng Support Channel của người gọi. Không có endpoint mới nào được xin (CAP_002).
 *  2. **`processingStatus` phải VẮNG ở tab "Tất cả", không phải rỗng.** Gửi `processingStatus=` là
 *     gửi một bộ lọc, và chính họ lỗi đó đã từng biến danh sách 1 dòng thành 0 dòng ở epic này
 *     (`referenceInfo=` khớp `IN ('')`). Nên object params được dựng có điều kiện, không đặt sẵn khoá
 *     rồi để undefined.
 *  3. **Bốn field OMNI có thể `null`** — `null` nghĩa là dòng đó không thuộc luồng Support Channel
 *     (endpoint dùng chung), không phải "giá trị mặc định".
 */
const OWN_ISSUES_PATH = "/api/v1/issues";

interface IWireOwnIssue {
  id: number;
  content: string;
  processingStatus: boolean;
  referenceInfo: string;
  issueTypeName: string;
  createdAt: string;
  supportCode?: string | null;
  status?: string | null;
  module?: string | null;
  hasCustomerReply?: boolean | null;
}

interface IWireOwnIssuesData {
  total: number;
  page: number;
  pageSize: number;
  issues?: IWireOwnIssue[] | null;
}

/** Ba tab của màn. `all` KHÔNG gửi `processingStatus`. */
export type OwnRequestsTab = "open" | "closed" | "all";

export interface IOwnRequestsParams {
  tab: OwnRequestsTab;
  page: number;
  pageSize: number;
}

export interface IOwnRequestsResult {
  items: ISupportRequest[];
  total: number;
  page: number;
  pageSize: number;
}

const toOwnRequest = (wire: IWireOwnIssue): ISupportRequest => ({
  // Mã rỗng là hợp lệ và hiếm: BFF để `supportCode` null khi không dựng được năm từ `createdAt`.
  // UI hiện `issueId` thay thế chứ không bịa một mã — người dùng đọc mã này cho Support.
  code: wire.supportCode || "",
  issueId: wire.id,
  status: toStatus(wire.status || ""),
  // Không có `status` trên wire ⇒ `toStatus` rơi về `sent`. Đánh dấu để UI KHÔNG hiện "đã gửi" như
  // một sự thật: xem doc comment của `statusUnknown`.
  statusUnknown: !wire.status,
  summary: wire.content,
  attachments: [],
  module: wire.module ? toModule(wire.module) : undefined,
  sentAt: wire.createdAt,
  // Giữ nguyên cả ba trạng thái: null của server → undefined của FE = CHƯA BIẾT.
  hasSupportReply:
    wire.hasCustomerReply === null || wire.hasCustomerReply === undefined ? undefined : wire.hasCustomerReply,
});

const listOwnRequests = async (params: IOwnRequestsParams): Promise<IOwnRequestsResult> => {
  const query: Record<string, string | number | boolean> = {
    flow: "support_channel",
    page: params.page,
    pageSize: params.pageSize,
  };
  // Chỉ đặt khoá khi thật sự có bộ lọc — xem điểm (2) ở doc comment trên.
  if (params.tab === "open") query.processingStatus = false;
  if (params.tab === "closed") query.processingStatus = true;

  const { data: envelope } = await staffBffClient.get<IEnvelope<IWireOwnIssuesData>>(OWN_ISSUES_PATH, {
    params: query,
  });
  const data = envelope.data;
  return {
    items: (data.issues || []).map(toOwnRequest),
    total: data.total >= 0 ? data.total : 0,
    page: data.page || params.page,
    pageSize: data.pageSize || params.pageSize,
  };
};

/** B6 — sự kiện đo. Best-effort theo hợp đồng: mất một counter không được làm hỏng một lượt đang chạy. */
const trackEvent = (eventType: string, payload: Record<string, unknown> = {}): void => {
  staffBffClient.post(`${STAFF_SELLER_BASE}/events`, { eventType, ...payload }).catch(() => undefined);
};

/**
 * Mã support → `issueId`.
 *
 * BFF dựng mã theo `SP-<năm tạo>-<issueId>` (`support_code.go`), nên id NẰM SẴN trong mã và không cần
 * một vòng tra cứu nào. Điều đó cho phép URL mang mã người đọc được (`/support/requests/SP-2026-42`)
 * trong khi mọi lời gọi vẫn key theo id (L1-013).
 *
 * Chỗ này CÓ đôi một chút với định dạng mã của BFF — và đó là lý do nó nằm ở đây, cạnh phần code duy
 * nhất trong FE được phép biết hình dạng wire, chứ không rải trong page. Sai định dạng ⇒ undefined ⇒
 * màn hình xử lý như not-found, không đoán bừa một id.
 */
export const issueIdFromSupportCode = (code: string): number | undefined => {
  const matched = /^SP-\d{4}-(\d+)$/i.exec((code || "").trim());
  if (!matched) return undefined;
  const id = Number(matched[1]);
  return id > 0 ? id : undefined;
};

/* ------------------------------------------------------------------ B7 · B8 · B9 · B10 · B11 (internal) */

export interface IInternalListParams {
  tab?: string;
  sellerId?: number;
  module?: PilotModule;
  /** Từ vựng của FE. Nó được dịch sang từ vựng contract ngay trước khi lên dây — xem `STATUS_TO_WIRE`. */
  status?: SupportRequestStatus;
  searchText?: string;
  isGetTotalRecord?: boolean;
  page?: number;
  pageSize?: number;
}

export interface IInternalListResult {
  items: ISupportRequest[];
  session: IInternalSessionScope;
  /** undefined = CHƯA ĐẾM (server trả -1). Không phải 0. */
  total?: number;
  /** undefined khi không xin đếm. */
  counts?: { open: number; unassigned: number; mine: number; done: number };
  page: number;
  pageSize: number;
}

const EMPTY_SESSION: IInternalSessionScope = { capabilities: [], allowedSellers: [] };

/**
 * B7 — danh sách phía Support.
 *
 * `session` luôn được trả kèm và luôn được dùng: đó là chỗ DUY NHẤT FE đọc được năng lực + danh mục
 * seller của phiên. `allowedSellers` rỗng là hợp lệ và nghĩa là "không thấy dòng nào" — không bao giờ
 * là "thấy tất cả".
 */
const listInternalRequests = async (params: IInternalListParams): Promise<IInternalListResult> => {
  // Dịch từ vựng ĐÚNG TẠI BIÊN dây, không sớm hơn: `hooks/` và component chỉ được biết từ vựng FE.
  const { data: envelope } = await staffBffClient.get<IEnvelope<IWireListData>>(INTERNAL_BASE, {
    params: { ...params, status: params.status ? STATUS_TO_WIRE[params.status] : undefined },
  });
  const data = envelope.data;
  return {
    items: (data.requests || []).map(toListItem),
    session: data.session || EMPTY_SESSION,
    // -1 nghĩa là CHƯA ĐẾM. Cho nó ra UI thì màn hình hiện "-1 kết quả"; chặn tại đây.
    total: data.total >= 0 ? data.total : undefined,
    counts: data.tabCounts || undefined,
    page: data.page,
    pageSize: data.pageSize,
  };
};

/** B8 — chi tiết cho màn nội bộ. Khác B4 ở chỗ đọc được yêu cầu của người khác + có đính kèm. */
const getInternalRequest = async (issueId: number): Promise<ISupportRequest> => {
  const { data: envelope } = await staffBffClient.get<IEnvelope<IWireInternalDetail>>(`${INTERNAL_BASE}/${issueId}`);
  return toInternalRequest(envelope.data);
};

/**
 * Ba lệnh chuyển trạng thái của BP3. Cả ba trả về status view TÍNH TỪ DÒNG SAU KHI GHI, nên caller
 * nên lấy trạng thái từ kết quả chứ đừng tự đoán trạng thái kế tiếp.
 */
const runTransition = async (issueId: number, action: "accept" | "reply" | "complete", body?: unknown) => {
  const { data: envelope } = await staffBffClient.post<IEnvelope<{ issueId: number; status: string }>>(
    `${INTERNAL_BASE}/${issueId}/${action}`,
    body
  );
  return { issueId: envelope.data.issueId, status: toStatus(envelope.data.status) };
};

/**
 * B13 — tra cứu hai lớp của màn nội bộ (T47b).
 *
 * `issueId` bỏ trống = tra cứu ĐỘC LẬP, không gắn phiếu nào (BR-BP3-13). `sellerId` là ngữ cảnh
 * seller của tình huống, không phải khoá tenant của phiên: BFF kiểm nó thuộc phạm vi phiên rồi mới
 * dùng, và khi có `issueId` thì BFF lấy seller TỪ PHIẾU và bỏ qua giá trị client gửi (lệch ⇒ 404).
 * Vì vậy ở đây chỉ gửi khi người dùng thực sự chọn — không bao giờ tự suy ra rồi gửi kèm.
 */
const lookupInternal = async (params: {
  question: string;
  issueId?: number;
  sellerId?: number;
  module?: PilotModule;
  conversationId?: string;
}): Promise<IInternalLookup> => {
  const { data: envelope } = await staffBffClient.post<IEnvelope<IWireLookupData>>(
    INTERNAL_LOOKUP_PATH,
    {
      question: params.question,
      issueId: params.issueId || undefined,
      sellerId: params.sellerId || undefined,
      module: params.module || undefined,
      conversationId: params.conversationId || undefined,
    },
    { timeout: INTERNAL_LOOKUP_TIMEOUT_MS }
  );
  const data = envelope.data;
  return {
    analysis: toLookupLayer(data.internalAnalysis),
    // Server đã cưỡng chế "lớp 1 `limited` ⇒ lớp 2 null". Kiểm lại ở đây một lần nữa là rẻ, và nó
    // chặn đúng cái sai tệ nhất: hiện một câu trả lời soạn sẵn bên cạnh một phân tích tự nhận là
    // chưa kết luận được.
    suggestion:
      data.internalAnalysis && data.internalAnalysis.disposition !== "limited" && data.suggestedReply
        ? toLookupLayer(data.suggestedReply)
        : undefined,
    // Chuỗi rỗng bị quy về undefined: gửi "" lên B10 sẽ bị đếm thành "đã dùng gợi ý" (BR-14).
    suggestionId: (data.suggestionId || "").trim() || undefined,
    conversationId: data.conversationId,
  };
};

/** B9 — tiếp nhận. Bấm hai lần là idempotent ở BFF, nên FE không cần tự chặn lần bấm thứ hai. */
const acceptRequest = (issueId: number) => runTransition(issueId, "accept");

/**
 * B10 — gửi phản hồi chính thức.
 *
 * `replyText` là bản NGƯỜI đã gõ/sửa (BR-BP3-05) — không có nhánh nào gửi thẳng gợi ý AI.
 * `suggestionId` có ĐÚNG hai trạng thái: một con trỏ thật, hoặc không gửi. Chuỗi rỗng là trạng thái
 * thứ ba không được phép, vì nó sẽ bị đếm thành "đã dùng gợi ý" và làm sai số đo BR-14.
 */
const replyRequest = (issueId: number, replyText: string, suggestionId?: string) =>
  runTransition(issueId, "reply", { replyText, suggestionId: suggestionId || undefined });

/** B11 — đánh dấu hoàn thành. Chỉ mở sau khi đã gửi phản hồi; BFF từ chối 409 nếu chưa. */
const completeRequest = (issueId: number) => runTransition(issueId, "complete");

export default {
  ask,
  uploadAttachment,
  lookupInternal,
  draftSummary,
  submitRequest,
  getMyRequest,
  listOwnRequests,
  trackEvent,
  listInternalRequests,
  getInternalRequest,
  acceptRequest,
  replyRequest,
  completeRequest,
};
